import { useQuery } from "@tanstack/react-query";
import ApiRequest from "../ApiRequest";
import { requestURLs } from "@/Environment";
import { QUERY_KEYS } from "./querykeys";

export interface DeliverableDetailData {
  deliverableId: string;
  deliverableNameId: string;
  projectId: string;
  organisationId: string;
  name: string;
  reference?: string | null;
  status: "NOT_STARTED" | "IN_PROGRESS" | "DELIVERED" | "APPROVED" | "REWORK_REQUIRED" | "CANCELLED";
  deliveryDueDate: string;
  approvalDueDate?: string | null;
  approvalRequired: boolean;
  actualDeliveryDate?: string | null;
  actualApprovalDate?: string | null;
  approvedVersionId?: string | null;
  description?: string | null;
  acceptanceCriteria?: string | null;
  createdAt: string;
  updatedAt: string;
  destination: { listValueId: string; value: string };
  type?: { listValueId: string; value: string } | null;
  owner: { userId: string; firstName: string; lastName: string; email: string };
  approver?: { userId: string; firstName: string; lastName: string; email: string } | null;
  contributors: Array<{
    deliverableContributorId: string;
    userId: string;
    user: { userId: string; firstName: string; lastName: string; email: string };
  }>;
  tasks: Array<{
    deliverableTaskId: string;
    task: {
      taskId: string;
      wbsId?: string | null;
      taskName: string;
      startDate: string;
      dueDate?: string | null;
      status: string;
      completionPecentage?: number | null;
      assignedUsers?: Array<{
        user: { userId: string; firstName: string; lastName: string };
      }>;
    };
  }>;
  risks: Array<{
    deliverableRiskId: string;
    risk: {
      riskId: string;
      riskNameId: string;
      title: string;
      status: string;
      impact: string;
      probability: string;
    };
  }>;
  inputDocuments: Array<{
    documentId: string;
    documentType: "FILE" | "LINK";
    title: string;
    fileUrl?: string | null;
    fileSize?: number | null;
    mimeType?: string | null;
    linkUrl?: string | null;
    createdAt: string;
    createdByUser: { userId: string; firstName: string; lastName: string };
  }>;
  versions: Array<{
    versionId: string;
    versionNumber: string;
    versionNote?: string | null;
    documentType: "FILE" | "LINK" | "NONE";
    title?: string | null;
    fileUrl?: string | null;
    fileSize?: number | null;
    linkUrl?: string | null;
    status: "DRAFT" | "DELIVERED" | "CHANGES_REQUESTED" | "APPROVED";
    isCurrent: boolean;
    createdAt: string;
    createdByUser: { userId: string; firstName: string; lastName: string };
  }>;
  history: Array<{
    historyId: string;
    eventType: "DELIVERY" | "APPROVAL" | "CHANGE_REQUEST" | "STATUS_CHANGE" | "COMMENT";
    eventDate: string;
    comment?: string | null;
    requestedChanges?: string | null;
    recordedByUser: { userId: string; firstName: string; lastName: string };
    decisionMaker?: { userId: string; firstName: string; lastName: string } | null;
    version?: { versionId: string; versionNumber: string; title?: string | null } | null;
  }>;
  isDeliveryOverdue: boolean;
  deliveryOverdueDays: number;
  isApprovalOverdue: boolean;
  approvalOverdueDays: number;
  deliveryVarianceDays: number | null;
  approvalVarianceDays: number | null;
  isClosed: boolean;
}

export default function useDeliverableDetailQuery(deliverableId?: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.deliverableDetail, deliverableId],
    queryFn: async () => {
      if (!deliverableId) return null;
      const res = await ApiRequest.get<{ data: DeliverableDetailData }>(
        `${requestURLs.deliverables}/detail/${deliverableId}`
      );
      return res.data;
    },
    enabled: Boolean(deliverableId),
  });
}
